import React, { useState, useEffect, useCallback, useRef } from 'react';
import Sidebar from '../components/shared/Sidebar';
import useAuthStore from '../store/authStore';
import quarterlyReportService, { QuarterlyReport, CreateQuarterlyReportRequest } from '../services/quarterlyReportService';
import organisationSettingsService from '../services/organisationSettingsService';
import { parseAIResponse, processMarkdown } from '../utils/aiResponseParser';
import VisualizationRenderer, { VisualizationData } from '../components/csv/VisualizationRenderer';
import ChartErrorBoundary from '../components/csv/ChartErrorBoundary';
import html2canvas from 'html2canvas';
import './QuarterlyReportsPage.css';
import '../components/csv/AIReport.css';

const QuarterlyReportsPage: React.FC = () => {
  const { user } = useAuthStore();
  const [reports, setReports] = useState<QuarterlyReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedYear, setSelectedYear] = useState<number | null>(null); // null means "All Years"
  const [organizationLogo, setOrganizationLogo] = useState<string | null>(null);
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isComparativeModalOpen, setIsComparativeModalOpen] = useState(false);
  const [currentReport, setCurrentReport] = useState<QuarterlyReport | null>(null);
  
  // Ref for capturing report content as image
  const reportContentRef = useRef<HTMLDivElement>(null);
  const modalBodyRef = useRef<HTMLDivElement>(null);
  
  // Form state
  const [formData, setFormData] = useState<{
    year: number;
    reportType: 'month' | 'quarter' | 'half-year' | 'year';
    quarter: number;
    month: number;
    halfYear: number;
    reportText: string;
    includeUnscored: boolean;
  }>({
    year: new Date().getFullYear(),
    reportType: 'quarter',
    quarter: 1,
    month: 1,
    halfYear: 1, // 1 for H1, 2 for H2
    reportText: 'Report content will be added later.',
    includeUnscored: false
  });
  
  // Comparative report form state
  const [comparativeFormData, setComparativeFormData] = useState<{
    period1: {
      year: number;
      reportType: 'month' | 'quarter' | 'half-year' | 'year';
      quarter: number;
      month: number;
      halfYear: number;
    };
    period2: {
      year: number;
      reportType: 'month' | 'quarter' | 'half-year' | 'year';
      quarter: number;
      month: number;
      halfYear: number;
    };
    reportText: string;
    includeUnscored: boolean;
  }>({
    period1: {
      year: new Date().getFullYear() - 1,
      reportType: 'quarter',
      quarter: 1,
      month: 1,
      halfYear: 1
    },
    period2: {
      year: new Date().getFullYear(),
      reportType: 'quarter',
      quarter: 1,
      month: 1,
      halfYear: 1
    },
    reportText: 'Comparative report content will be added later.',
    includeUnscored: false
  });

  // Loading and success states
  const [submitting, setSubmitting] = useState(false);
  const [generatingWithAI, setGeneratingWithAI] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Load reports
  const loadReports = useCallback(async (year?: number | null) => {
    try {
      setLoading(true);
      // Pass undefined to get all reports if year is null
      const data = await quarterlyReportService.getQuarterlyReports(year || undefined);
      setReports(data);
      setError(null);
    } catch (err) {
      console.error('Error loading quarterly reports:', err);
      setError('Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load organization logo
  const loadOrganizationLogo = useCallback(async () => {
    try {
      const settings = await organisationSettingsService.getOrganisationSettings();
      if (settings.logoUrl) {
        // Construct full URL if it's a relative path
        // logoUrl is now something like /api/organisation-settings/logo/{orgId}
        const baseUrl = settings.logoUrl.startsWith('http') 
          ? settings.logoUrl 
          : `${(process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/api$/, '')}${settings.logoUrl}`;
        // Add cache-busting parameter to ensure fresh image load
        const logoUrl = `${baseUrl}?t=${Date.now()}`;
        setOrganizationLogo(logoUrl);
      }
    } catch (error) {
      console.error('Failed to load organization logo:', error);
    }
  }, []);

  // Load reports on component mount and when year changes
  useEffect(() => {
    loadReports(selectedYear);
    loadOrganizationLogo();
  }, [loadReports, selectedYear, loadOrganizationLogo]);

  // Listen for organization logo updates
  useEffect(() => {
    const handleLogoUpdate = (event: CustomEvent) => {
      console.log('QuarterlyReports: Organization logo updated event received:', event.detail);
      try {
        loadOrganizationLogo();
        console.log('QuarterlyReports: Logo refresh completed successfully');
      } catch (error) {
        console.error('QuarterlyReports: Error during logo refresh:', error);
      }
    };

    window.addEventListener('organizationLogoUpdated', handleLogoUpdate as EventListener);
    console.log('QuarterlyReports: organizationLogoUpdated event listener added');
    
    return () => {
      window.removeEventListener('organizationLogoUpdated', handleLogoUpdate as EventListener);
      console.log('QuarterlyReports: organizationLogoUpdated event listener removed');
    };
  }, [loadOrganizationLogo]);

  // Clear success message after 3 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Handle year filter change
  const handleYearChange = (value: string) => {
    const year = value === 'all' ? null : parseInt(value);
    setSelectedYear(year);
  };

  // Open modal for creating new report
  const handleCreateReport = () => {
    setFormData({
      year: selectedYear || new Date().getFullYear(), // Default to current year for new reports
      reportType: 'quarter' as const,
      quarter: 1,
      month: 1,
      halfYear: 1,
      reportText: 'Report content will be added later.',
      includeUnscored: false
    });
    setCurrentReport(null);
    setIsModalOpen(true);
  };

  // Open modal for viewing report in full screen
  const handleViewReport = (report: QuarterlyReport) => {
    setCurrentReport(report);
    setIsViewModalOpen(true);
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user?.organization_id) {
      setError('Organization ID not found');
      return;
    }

    setSubmitting(true);
    setError(null);

    // Check if we're generating with AI (no report text provided)
    const isAIGeneration = !formData.reportText || formData.reportText === 'Report content will be added later.';
    if (isAIGeneration) {
      setGeneratingWithAI(true);
    }

    try {
      // Create new report with proper parameters based on report type
      const reportRequest: CreateQuarterlyReportRequest = {
        organization_id: user.organization_id,
        year: formData.year,
        quarter: formData.reportType === 'quarter' ? formData.quarter : undefined,
        report_text: formData.reportText,
        reportType: formData.reportType,
        month: formData.reportType === 'month' ? formData.month : undefined,
        halfYear: formData.reportType === 'half-year' ? formData.halfYear : undefined
      };
      
      const result = await quarterlyReportService.createQuarterlyReport(reportRequest);
      if (result.success) {
        setSuccessMessage('Report generated successfully');
      } else {
        setError(result.message);
        setSubmitting(false);
        return;
      }
      
      setIsModalOpen(false);
      await loadReports(selectedYear);
    } catch (err: any) {
      console.error('Error saving quarterly report:', err);
      setError(err.message || 'Failed to save report');
    } finally {
      setSubmitting(false);
      setGeneratingWithAI(false);
    }
  };

  // Handle comparative report submission
  const handleComparativeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user?.organization_id) {
      setError('Organization ID not found');
      return;
    }

    setSubmitting(true);
    setError(null);

    // Always generate with AI for comparative reports
    setGeneratingWithAI(true);

    try {
      // For now, we'll create a single report with a special comparative format
      // Later we can extend this to be a proper comparative report type
      const period1Desc = getPeriodDescription(comparativeFormData.period1);
      const period2Desc = getPeriodDescription(comparativeFormData.period2);
      
      const reportRequest: CreateQuarterlyReportRequest = {
        organization_id: user.organization_id,
        year: comparativeFormData.period2.year, // Use period2 as the primary year
        quarter: comparativeFormData.period2.reportType === 'quarter' ? comparativeFormData.period2.quarter : undefined,
        report_text: 'Comparative report content will be added later.',
        reportType: 'comparative',
        // Add comparative-specific data
        comparativePeriod1: comparativeFormData.period1,
        comparativePeriod2: comparativeFormData.period2
      };
      
      const result = await quarterlyReportService.createQuarterlyReport(reportRequest);
      if (result.success) {
        setSuccessMessage('Comparative report generated successfully');
      } else {
        setError(result.message);
        setSubmitting(false);
        return;
      }
      
      setIsComparativeModalOpen(false);
      await loadReports(selectedYear);
      
    } catch (err: any) {
      console.error('Error saving comparative report:', err);
      setError(err.message || 'Failed to save comparative report');
    } finally {
      setSubmitting(false);
      setGeneratingWithAI(false);
    }
  };

  // Download report as image
  const downloadReportAsImage = async () => {
    if (!reportContentRef.current || !currentReport) {
      console.error('Report content not available for capture');
      return;
    }

    try {
      console.log('📸 Capturing report as image...');

      // Debug: Check element visibility and content
      const element = reportContentRef.current;
      const rect = element.getBoundingClientRect();
      const computedStyle = window.getComputedStyle(element);
      
      console.log('🔍 Element debug info:');
      console.log(`📐 Element dimensions: ${rect.width}x${rect.height}`);
      console.log(`📏 Scroll dimensions: ${element.scrollWidth}x${element.scrollHeight}`);
      console.log(`👁️ Visibility: ${computedStyle.visibility}`);
      console.log(`🎨 Display: ${computedStyle.display}`);
      console.log(`📦 Opacity: ${computedStyle.opacity}`);
      console.log(`🎯 Position: ${computedStyle.position}`);
      console.log(`📄 Content length: ${element.textContent?.length || 0} characters`);
      console.log(`🏗️ Child elements: ${element.children.length}`);

      // Wait for charts to fully render and clear any selection states
      console.log('⏳ Waiting 1 second for charts to fully render...');
      await new Promise(resolve => setTimeout(resolve, 1000));

      // No need for special styling - using inline-visualization class like AI report popup

      // Simple html2canvas configuration with delay - no DOM manipulation
      const canvas = await html2canvas(reportContentRef.current, {
        allowTaint: true,
        useCORS: true,
        scale: 1,
        backgroundColor: '#ffffff',
        logging: true,
        scrollX: 0,
        scrollY: 0,
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
        // Don't ignore anything - capture everything as-is
        ignoreElements: () => false
      });

      console.log(`📊 Canvas created: ${canvas.width}x${canvas.height}`);

      // No cleanup needed - using standard inline-visualization class

      // Check if canvas is actually populated
      if (canvas.width === 0 || canvas.height === 0) {
        console.warn('Canvas has zero dimensions, trying modal body capture...');
        
        // Fallback: try capturing the entire modal body
        if (modalBodyRef.current) {
          const modalCanvas = await html2canvas(modalBodyRef.current, {
            allowTaint: true,
            useCORS: true,
            scale: 1,
            backgroundColor: '#ffffff',
            logging: true,
            scrollX: 0,
            scrollY: 0,
            windowWidth: window.innerWidth,
            windowHeight: window.innerHeight,
            foreignObjectRendering: false,
            ignoreElements: () => false
          });
          
          if (modalCanvas.width > 0 && modalCanvas.height > 0) {
            console.log('✅ Fallback modal capture successful');
            // Use the modal canvas instead
            modalCanvas.toBlob((blob) => {
              if (!blob) {
                console.error('Failed to create fallback image blob');
                return;
              }
              
              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              
              const reportInfo = getReportDisplayInfo(currentReport);
              const filename = `${reportInfo.title.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.png`;
              link.download = filename;
              
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);
              
              console.log('✅ Fallback report image downloaded successfully');
            }, 'image/png', 1.0);
            return;
          }
        }
        
        throw new Error('Both content and modal capture failed - canvas has zero dimensions');
      }

      // Convert canvas to blob
      canvas.toBlob((blob) => {
        if (!blob) {
          console.error('Failed to create image blob');
          return;
        }

        console.log(`💾 Blob created: ${blob.size} bytes`);

        // Create download link
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        // Generate filename based on report info
        const reportInfo = getReportDisplayInfo(currentReport);
        const filename = `${reportInfo.title.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.png`;
        link.download = filename;
        
        // Trigger download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Clean up
        URL.revokeObjectURL(url);
        
        console.log('✅ Report image downloaded successfully');
      }, 'image/png', 1.0);

    } catch (error) {
      // No cleanup needed - using standard inline-visualization class
      
      console.error('❌ Error capturing report as image:', error);
      console.error('Error details:', error);
      alert(`Failed to download report as image: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // Helper function to get period description
  const getPeriodDescription = (period: typeof comparativeFormData.period1) => {
    if (period.reportType === 'month') {
      const monthNames = ['', 'January', 'February', 'March', 'April', 'May', 'June',
                         'July', 'August', 'September', 'October', 'November', 'December'];
      return `${monthNames[period.month]} ${period.year}`;
    } else if (period.reportType === 'quarter') {
      return `Q${period.quarter} ${period.year}`;
    } else if (period.reportType === 'half-year') {
      return `H${period.halfYear} ${period.year}`;
    } else if (period.reportType === 'year') {
      return `${period.year}`;
    }
    return '';
  };

  // Helper function to sync period 2 report type with period 1
  const syncPeriodTypes = (newPeriod1: typeof comparativeFormData.period1) => {
    setComparativeFormData(prev => ({
      ...prev,
      period1: newPeriod1,
      period2: {
        ...prev.period2,
        reportType: newPeriod1.reportType, // Always match period 1's report type
        // Keep the same specific period values for period 2
        quarter: newPeriod1.reportType === 'quarter' ? prev.period2.quarter : newPeriod1.quarter,
        month: newPeriod1.reportType === 'month' ? prev.period2.month : newPeriod1.month,
        halfYear: newPeriod1.reportType === 'half-year' ? prev.period2.halfYear : newPeriod1.halfYear
      }
    }));
  };

  // Handle delete report
  const handleDeleteReport = async (report: QuarterlyReport) => {
    if (!window.confirm(`Are you sure you want to delete the Q${report.quarter} ${report.year} report?`)) {
      return;
    }

    try {
      const result = await quarterlyReportService.deleteQuarterlyReport(report.id!);
      if (result.success) {
        setSuccessMessage('Report deleted successfully');
        await loadReports(selectedYear);
      } else {
        setError(result.message);
      }
    } catch (err: any) {
      console.error('Error deleting quarterly report:', err);
      setError(err.message || 'Failed to delete report');
    }
  };

  // Get available years (current year ± 5 years)
  const currentYear = new Date().getFullYear();
  const availableYears = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);

  // Detect report type from content and get appropriate display name
  const getReportDisplayInfo = (report: QuarterlyReport) => {
    const reportText = report.reportText.toLowerCase();
    
    // Check if it's a comparative report first - look for strong comparative indicators
    const hasStrongComparativeIndicators = (
      reportText.includes('vs q') || // "vs Q1", "vs Q2", etc.
      reportText.includes('q1 2023 vs q1 2024') ||
      reportText.includes('comparative report') ||
      reportText.includes('comparative analysis') ||
      (reportText.includes('period 1') && reportText.includes('period 2')) ||
      (reportText.includes('vs') && reportText.includes('202') && reportText.match(/vs.*202\d/)) // "vs 2023", "vs 2024"
    );
    
    if (hasStrongComparativeIndicators) {
      // Try to extract the periods being compared from the text
      const yearMatches = reportText.match(/(\d{4})/g);
      if (yearMatches && yearMatches.length >= 2) {
        const year1 = yearMatches[0];
        const year2 = yearMatches[yearMatches.length - 1];
        return {
          badge: 'COMP',
          title: `Comparative Report: ${year1} vs ${year2}`,
          shortTitle: `Comparative ${year1}-${year2}`
        };
      }
      
      // Fallback for comparative reports without clear year extraction
      return {
        badge: 'COMP',
        title: `Comparative Report ${report.year}`,
        shortTitle: 'Comparative Analysis'
      };
    }
    
    // Check if it's an annual report (but not if it has strong comparative indicators)
    if (reportText.includes('annual') || 
        (reportText.includes('year') && reportText.includes('business')) ||
        reportText.includes('annual business report')) {
      return {
        badge: report.year.toString(),
        title: `${report.year} Annual Report`,
        shortTitle: 'Annual'
      };
    }
    
    // Check if it's a comparative report - weaker indicators
    const isComparative = (
      (reportText.includes('comparative') && !reportText.includes('annual')) || 
      (reportText.includes('comparison') && reportText.includes('period')) || 
      (reportText.includes('vs') && reportText.includes('period') && !reportText.includes('annual')) ||
      reportText.includes('compared to') ||
      (reportText.includes('baseline') && reportText.includes('comparison') && !reportText.includes('annual'))
    );
    
    if (isComparative) {
      
      // Try to extract the periods being compared from the text
      const yearMatches = reportText.match(/(\d{4})/g);
      if (yearMatches && yearMatches.length >= 2) {
        const year1 = yearMatches[0];
        const year2 = yearMatches[yearMatches.length - 1];
        return {
          badge: 'COMP',
          title: `Comparative Report: ${year1} vs ${year2}`,
          shortTitle: `Comparative ${year1}-${year2}`
        };
      }
      
      // Fallback for comparative reports without clear year extraction
      return {
        badge: 'COMP',
        title: `Comparative Report ${report.year}`,
        shortTitle: 'Comparative Analysis'
      };
    }
    
    // Check if it's a half-year report
    if (reportText.includes('half-year') || reportText.includes('h1') || reportText.includes('h2')) {
      const isH1 = reportText.includes('h1') || reportText.includes('first half');
      const halfYear = isH1 ? 'H1' : 'H2';
      const months = isH1 ? 'Jan-Jun' : 'Jul-Dec';
      return {
        badge: halfYear,
        title: `${halfYear} ${report.year} Report`,
        shortTitle: `${halfYear} ${months}`
      };
    }
    
    // Check if it's explicitly a quarterly report first (higher priority)
    if (reportText.includes('quarterly') || reportText.includes('quarter') || 
        reportText.match(/q[1-4]/i)) {
      const quarter = report.quarter;
      return {
        badge: `Q${quarter}`,
        title: `Q${quarter} ${report.year} Report`,
        shortTitle: `Q${quarter} ${quarter === 1 ? 'Jan-Mar' : quarter === 2 ? 'Apr-Jun' : quarter === 3 ? 'Jul-Sep' : 'Oct-Dec'}`
      };
    }
    
    // Check if it's a monthly report (only if not quarterly)
    const monthNames = ['january', 'february', 'march', 'april', 'may', 'june',
                       'july', 'august', 'september', 'october', 'november', 'december'];
    
    // Only consider it monthly if it contains "monthly" keyword or explicit month indicators
    if (reportText.includes('monthly') || reportText.includes('month report')) {
      for (let i = 0; i < monthNames.length; i++) {
        if (reportText.includes(monthNames[i])) {
          const monthName = monthNames[i].charAt(0).toUpperCase() + monthNames[i].slice(1);
          return {
            badge: monthName.slice(0, 3),
            title: `${monthName} ${report.year} Report`,
            shortTitle: monthName
          };
        }
      }
    }
    
    // Default to quarterly based on quarter field
    const quarter = report.quarter;
    return {
      badge: `Q${quarter}`,
      title: `Q${quarter} ${report.year} Report`,
      shortTitle: `Q${quarter} ${quarter === 1 ? 'Jan-Mar' : quarter === 2 ? 'Apr-Jun' : quarter === 3 ? 'Jul-Sep' : 'Oct-Dec'}`
    };
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="dashboard-page">
      <Sidebar />
      
      <div className="dashboard-main">
        <div className="dashboard-content">
          <div className="breadcrumbs">
            <span className="breadcrumb-current">Reports</span>
          </div>
          
        <div className="quarterly-reports-header">
          <div className="header-top">
            <h1>Reports</h1>
            <div className="header-actions">
              <button 
                className="create-report-btn"
                onClick={handleCreateReport}
              >
                <span className="icon-plus"></span>
                Generate Report
              </button>
              <button 
                className="create-report-btn comparative-btn"
                onClick={() => setIsComparativeModalOpen(true)}
              >
                <span className="icon-compare"></span>
                Generate Comparative Report
              </button>
            </div>
          </div>
          
          {/* Year Filter */}
          <div className="filters-section">
            <div className="filter-group">
              <label htmlFor="year-select">Year:</label>
              <select 
                id="year-select"
                value={selectedYear === null ? 'all' : selectedYear} 
                onChange={(e) => handleYearChange(e.target.value)}
                className="year-select"
              >
                <option value="all">All Years</option>
                {availableYears.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="success-message">
            <span className="icon-check-circle"></span>
            {successMessage}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="error-message">
            <span className="icon-alert-circle"></span>
            {error}
          </div>
        )}

        {/* Reports Grid */}
        <div className="reports-section">
          <div className="reports-grid-container" style={{ minHeight: loading ? '200px' : 'auto' }}>
            {loading ? (
              <div className="loading-overlay">
                <div className="loading-spinner">
                  <div className="spinner"></div>
                  <span>Loading reports...</span>
                </div>
              </div>
            ) : null}
            
            {!loading && reports.length === 0 ? (
              <div className="no-reports">
                <div className="no-reports-icon">📊</div>
                <h3>No reports found</h3>
                <p>Create your first report{selectedYear ? ` for ${selectedYear}` : ''}</p>
                <button 
                  className="create-first-report-btn"
                  onClick={handleCreateReport}
                >
                  Generate Report
                </button>
              </div>
            ) : null}
            
            {!loading && reports.length > 0 ? (
              <div className="reports-grid">
                {reports.map((report) => {
                  const displayInfo = getReportDisplayInfo(report);
                  return (
                    <div key={report.id} className="report-card" onClick={() => handleViewReport(report)}>
                      <div className="report-card-header">
                        <div className={`quarter-badge ${displayInfo.badge === 'COMP' ? 'comparative' : ''}`}>
                          {displayInfo.badge}
                        </div>
                        <div className="report-year">{report.year}</div>
                        <div className="report-actions">
                          <button 
                            className="delete-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteReport(report);
                            }}
                            title="Delete Report"
                          >
                            <span className="icon-trash"></span>
                          </button>
                        </div>
                      </div>
                      
                      <div className="report-card-content">
                        <h3>{displayInfo.shortTitle}</h3>
                      <div className="report-preview">
                        {report.reportText.length > 200 
                          ? `${report.reportText.substring(0, 200)}...` 
                          : report.reportText
                        }
                      </div>
                      <div className="report-meta">
                        <span className="created-date">
                          Created: {formatDate(report.createdAt!)}
                        </span>
                        {report.updatedAt !== report.createdAt && (
                          <span className="updated-date">
                            Updated: {formatDate(report.updatedAt!)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            ) : null}
          </div>
        </div>

        {/* Create/Edit Modal */}
        {isModalOpen && (
          <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Generate Report</h2>
                <button 
                  className="modal-close-btn"
                  onClick={() => setIsModalOpen(false)}
                >
                  <span className="icon-x"></span>
                </button>
              </div>
              
              <div className="modal-description">
                <p>Lumina AI will analyze all your financial submissions for the selected quarter and generate a comprehensive business report including performance analysis, trends, and strategic recommendations.</p>
              </div>
              
              <form onSubmit={handleSubmit} className="report-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="form-year">Year</label>
                    <select
                      id="form-year"
                      value={formData.year}
                      onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                      required
                    >
                      {availableYears.map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="form-report-type">Report Period</label>
                    <select
                      id="form-report-type"
                      value={formData.reportType}
                      onChange={(e) => setFormData({ ...formData, reportType: e.target.value as 'month' | 'quarter' | 'half-year' | 'year' })}
                      required
                    >
                      <option value="month">Monthly Report</option>
                      <option value="quarter">Quarterly Report</option>
                      <option value="half-year">Half-Year Report</option>
                      <option value="year">Annual Report</option>
                    </select>
                  </div>
                  
                  {formData.reportType === 'month' && (
                    <div className="form-group">
                      <label htmlFor="form-month">Month</label>
                      <select
                        id="form-month"
                        value={formData.month}
                        onChange={(e) => setFormData({ ...formData, month: parseInt(e.target.value) })}
                        required
                      >
                        <option value={1}>January</option>
                        <option value={2}>February</option>
                        <option value={3}>March</option>
                        <option value={4}>April</option>
                        <option value={5}>May</option>
                        <option value={6}>June</option>
                        <option value={7}>July</option>
                        <option value={8}>August</option>
                        <option value={9}>September</option>
                        <option value={10}>October</option>
                        <option value={11}>November</option>
                        <option value={12}>December</option>
                      </select>
                    </div>
                  )}
                  
                  {formData.reportType === 'quarter' && (
                    <div className="form-group">
                      <label htmlFor="form-quarter">Quarter</label>
                      <select
                        id="form-quarter"
                        value={formData.quarter}
                        onChange={(e) => setFormData({ ...formData, quarter: parseInt(e.target.value) })}
                        required
                      >
                        <option value={1}>Q1 (Jan-Mar)</option>
                        <option value={2}>Q2 (Apr-Jun)</option>
                        <option value={3}>Q3 (Jul-Sep)</option>
                        <option value={4}>Q4 (Oct-Dec)</option>
                      </select>
                    </div>
                  )}
                  
                  {formData.reportType === 'half-year' && (
                    <div className="form-group">
                      <label htmlFor="form-half-year">Half Year</label>
                      <select
                        id="form-half-year"
                        value={formData.halfYear}
                        onChange={(e) => setFormData({ ...formData, halfYear: parseInt(e.target.value) })}
                        required
                      >
                        <option value={1}>H1 (Jan-Jun)</option>
                        <option value={2}>H2 (Jul-Dec)</option>
                      </select>
                    </div>
                  )}
                </div>
                
                {/* Include unscored submissions checkbox */}
                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.includeUnscored}
                      onChange={(e) => setFormData({ ...formData, includeUnscored: e.target.checked })}
                    />
                    <span className="checkbox-text">
                      Include submissions without a quality score
                    </span>
                  </label>
                  <div className="checkbox-help">
                    When checked, submissions that haven't been quality scored will be included in the report analysis.
                  </div>
                </div>
                
                {/* Report Content field hidden for now */}
                <input
                  type="hidden"
                  value={formData.reportText || 'Report content will be added later.'}
                />
                
                <div className="form-actions">
                  <button 
                    type="button" 
                    className="cancel-btn"
                    onClick={() => setIsModalOpen(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="submit-btn"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <div className="btn-spinner"></div>
                        {generatingWithAI ? 'Analyzing data with Lumina AI...' : 'Generating...'}
                      </>
                    ) : (
                      'Generate Report'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Full-Screen View Modal */}
        {isViewModalOpen && currentReport && (
          <div className="fullscreen-modal-overlay" onClick={() => setIsViewModalOpen(false)}>
            <div className="fullscreen-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="fullscreen-modal-header">
                <div className="fullscreen-header-info">
                  <h1>{getReportDisplayInfo(currentReport).title}</h1>
                  <div className="fullscreen-report-meta">
                    <span>Created: {formatDate(currentReport.createdAt!)}</span>
                    {currentReport.updatedAt !== currentReport.createdAt && (
                      <span>Updated: {formatDate(currentReport.updatedAt!)}</span>
                    )}
                  </div>
                </div>
                <div className="fullscreen-header-actions">
                  {organizationLogo && (
                    <div className="report-organization-logo">
                      <img 
                        src={organizationLogo} 
                        alt="Organization Logo" 
                        className="report-logo-image"
                      />
                    </div>
                  )}
                  <button 
                    className="download-image-btn"
                    onClick={downloadReportAsImage}
                    title="Download as Image"
                  >
                    <span className="icon-download"></span>
                    Download as Image
                  </button>
                  <button 
                    className="fullscreen-close-btn"
                    onClick={() => setIsViewModalOpen(false)}
                    title="Close"
                  >
                    <span className="icon-x"></span>
                  </button>
                </div>
              </div>
              
              <div className="fullscreen-modal-body" ref={modalBodyRef}>
                <div className="fullscreen-report-content" ref={reportContentRef}>
                  {(() => {
                    // Parse the AI response to extract text and visualizations
                    const { sections } = parseAIResponse(currentReport.reportText || '');
                    
                    if (sections && sections.length > 0) {
                      return sections.map((section, idx) =>
                        section.type === 'text' ? (
                          <div 
                            key={idx} 
                            className="report-text-section" 
                            dangerouslySetInnerHTML={{ __html: processMarkdown(section.content as string) }} 
                          />
                        ) : (
                          <div key={idx} className="inline-visualization">
                            <ChartErrorBoundary chartId={`quarterly-report-chart-${idx}`}>
                              <VisualizationRenderer 
                                data={section.content as VisualizationData} 
                                halfSize={false} 
                                disableInteraction={true}
                              />
                            </ChartErrorBoundary>
                          </div>
                        )
                      );
                    } else {
                      // Fallback: process as plain markdown if no sections found
                      return (
                        <div 
                          className="report-text-section" 
                          dangerouslySetInnerHTML={{ __html: processMarkdown(currentReport.reportText) }} 
                        />
                      );
                    }
                  })()}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Comparative Report Modal */}
        {isComparativeModalOpen && (
          <div className="modal-overlay" onClick={() => setIsComparativeModalOpen(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Generate Comparative Report</h2>
                <button 
                  className="close-btn" 
                  onClick={() => setIsComparativeModalOpen(false)}
                >
                  ×
                </button>
              </div>
              
              <form onSubmit={handleComparativeSubmit}>
                <div className="modal-body">
                  <div className="comparative-periods">
                    {/* Period 1 */}
                    <div className="period-section">
                      <h3>Period 1 (Baseline)</h3>
                      <div className="form-group">
                        <label htmlFor="period1-year">Year</label>
                        <select
                          id="period1-year"
                          value={comparativeFormData.period1.year}
                          onChange={(e) => syncPeriodTypes({
                            ...comparativeFormData.period1,
                            year: parseInt(e.target.value)
                          })}
                          required
                        >
                          {availableYears.map(year => (
                            <option key={year} value={year}>{year}</option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group">
                        <label htmlFor="period1-report-type">Report Period</label>
                        <select
                          id="period1-report-type"
                          value={comparativeFormData.period1.reportType}
                          onChange={(e) => syncPeriodTypes({
                            ...comparativeFormData.period1,
                            reportType: e.target.value as 'month' | 'quarter' | 'half-year' | 'year'
                          })}
                          required
                        >
                          <option value="month">Monthly Report</option>
                          <option value="quarter">Quarterly Report</option>
                          <option value="half-year">Half-Year Report</option>
                          <option value="year">Annual Report</option>
                        </select>
                      </div>

                      {comparativeFormData.period1.reportType === 'month' && (
                        <div className="form-group">
                          <label htmlFor="period1-month">Month</label>
                          <select
                            id="period1-month"
                            value={comparativeFormData.period1.month}
                            onChange={(e) => syncPeriodTypes({
                              ...comparativeFormData.period1,
                              month: parseInt(e.target.value)
                            })}
                            required
                          >
                            {Array.from({ length: 12 }, (_, i) => (
                              <option key={i + 1} value={i + 1}>
                                {new Date(0, i).toLocaleString('en', { month: 'long' })}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {comparativeFormData.period1.reportType === 'quarter' && (
                        <div className="form-group">
                          <label htmlFor="period1-quarter">Quarter</label>
                          <select
                            id="period1-quarter"
                            value={comparativeFormData.period1.quarter}
                            onChange={(e) => syncPeriodTypes({
                              ...comparativeFormData.period1,
                              quarter: parseInt(e.target.value)
                            })}
                            required
                          >
                            <option value={1}>Q1 (Jan-Mar)</option>
                            <option value={2}>Q2 (Apr-Jun)</option>
                            <option value={3}>Q3 (Jul-Sep)</option>
                            <option value={4}>Q4 (Oct-Dec)</option>
                          </select>
                        </div>
                      )}

                      {comparativeFormData.period1.reportType === 'half-year' && (
                        <div className="form-group">
                          <label htmlFor="period1-half-year">Half Year</label>
                          <select
                            id="period1-half-year"
                            value={comparativeFormData.period1.halfYear}
                            onChange={(e) => syncPeriodTypes({
                              ...comparativeFormData.period1,
                              halfYear: parseInt(e.target.value)
                            })}
                            required
                          >
                            <option value={1}>H1 (Jan-Jun)</option>
                            <option value={2}>H2 (Jul-Dec)</option>
                          </select>
                        </div>
                      )}
                    </div>

                    {/* Period 2 */}
                    <div className="period-section">
                      <h3>Period 2 (Comparison)</h3>
                      <div className="form-group">
                        <label htmlFor="period2-year">Year</label>
                        <select
                          id="period2-year"
                          value={comparativeFormData.period2.year}
                          onChange={(e) => setComparativeFormData({
                            ...comparativeFormData,
                            period2: { ...comparativeFormData.period2, year: parseInt(e.target.value) }
                          })}
                          required
                        >
                          {availableYears.map(year => (
                            <option key={year} value={year}>{year}</option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group">
                        <label htmlFor="period2-report-type">Report Period</label>
                        <select
                          id="period2-report-type"
                          value={comparativeFormData.period2.reportType}
                          disabled
                          style={{ opacity: 0.6, cursor: 'not-allowed' }}
                        >
                          <option value="month">Monthly Report</option>
                          <option value="quarter">Quarterly Report</option>
                          <option value="half-year">Half-Year Report</option>
                          <option value="year">Annual Report</option>
                        </select>

                      </div>

                      {comparativeFormData.period2.reportType === 'month' && (
                        <div className="form-group">
                          <label htmlFor="period2-month">Month</label>
                          <select
                            id="period2-month"
                            value={comparativeFormData.period2.month}
                            onChange={(e) => setComparativeFormData({
                              ...comparativeFormData,
                              period2: { ...comparativeFormData.period2, month: parseInt(e.target.value) }
                            })}
                            required
                          >
                            {Array.from({ length: 12 }, (_, i) => (
                              <option key={i + 1} value={i + 1}>
                                {new Date(0, i).toLocaleString('en', { month: 'long' })}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {comparativeFormData.period2.reportType === 'quarter' && (
                        <div className="form-group">
                          <label htmlFor="period2-quarter">Quarter</label>
                          <select
                            id="period2-quarter"
                            value={comparativeFormData.period2.quarter}
                            onChange={(e) => setComparativeFormData({
                              ...comparativeFormData,
                              period2: { ...comparativeFormData.period2, quarter: parseInt(e.target.value) }
                            })}
                            required
                          >
                            <option value={1}>Q1 (Jan-Mar)</option>
                            <option value={2}>Q2 (Apr-Jun)</option>
                            <option value={3}>Q3 (Jul-Sep)</option>
                            <option value={4}>Q4 (Oct-Dec)</option>
                          </select>
                        </div>
                      )}

                      {comparativeFormData.period2.reportType === 'half-year' && (
                        <div className="form-group">
                          <label htmlFor="period2-half-year">Half Year</label>
                          <select
                            id="period2-half-year"
                            value={comparativeFormData.period2.halfYear}
                            onChange={(e) => setComparativeFormData({
                              ...comparativeFormData,
                              period2: { ...comparativeFormData.period2, halfYear: parseInt(e.target.value) }
                            })}
                            required
                          >
                            <option value={1}>H1 (Jan-Jun)</option>
                            <option value={2}>H2 (Jul-Dec)</option>
                          </select>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={comparativeFormData.includeUnscored}
                        onChange={(e) => setComparativeFormData({
                          ...comparativeFormData,
                          includeUnscored: e.target.checked
                        })}
                      />
                      <span className="checkbox-text">Include submissions without quality score</span>
                      <span className="checkbox-help">Include data submissions that haven't been quality assessed</span>
                    </label>
                  </div>


                </div>
                
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-secondary"
                    onClick={() => setIsComparativeModalOpen(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={submitting}
                  >
                    {generatingWithAI ? 'Generating with AI...' : submitting ? 'Creating...' : 'Generate Comparative Report'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default QuarterlyReportsPage;
